import JoditEditor from "jodit-react";
import "./rich-text-editor.css";
import { useMemo, useRef, useState } from "react";
import { Button } from "../ui/button";
import { AIAssistant } from "./ai-assistant";

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  required?: boolean;
  title?: string;
  className?: string;
  minHeight?: number;
  maxHeight?: number;
  editorConfig?: any;
  showBorder?: boolean;
  enableAI?: boolean;
  aiContext?: string;
  /** Show image toolbar button for inserting images by URL (no file upload). */
  enableImageUrl?: boolean;
}

export function RichTextEditor({
  value,
  onChange,
  error,
  title,
  className,
  minHeight = 220,
  maxHeight = 800,
  editorConfig,
  showBorder = true,
  enableAI = false,
  aiContext,
  enableImageUrl = false,
}: RichTextEditorProps) {
  const editor = useRef<any>(null);
  const [showFullToolbar, setShowFullToolbar] = useState(false);

  const config = useMemo(() => {
    const basicToolbar = [
      "bold",
      "italic",
      "underline",
      "|",
      "ul",
      "ol",
      "|",
      "font",
      "fontsize",
      "paragraph",
      "|",
      "align",
      "|",
      "link",
      ...(enableImageUrl ? ["image"] : []),
      "|",
      "undo",
      "redo",
    ];

    const defaultConfig = {
      // Basic settings
      readonly: false,
      language: "en",
      theme: "default",
      direction: "ltr",

      /**
       * Avoid iframe mode so the editable surface reliably
       * occupies height (and remains clickable) inside complex layouts.
       */
      iframe: false,
      
      // Resize configuration
      allowResizeY: true,
      allowResizeX: false,
      minHeight,
      maxHeight,
      height: minHeight,
      
      // UI settings
      showXPathInStatusbar: false,
      toolbarStickyOffset: 50,
      inline: false,
      zIndex: 100,
      
      // Content settings
      enter: "div",
      /**
       * Paste HTML as real markup (not escaped text).
       * Previously `insert_as_text` made `<p>` / `<h2>` show as literal tags
       * on save and on the student blog.
       */
      defaultActionOnPaste: "insert_as_html",
      askBeforePasteHTML: true,
      askBeforePasteFromWord: true,
      
      // Styling
      style: {
        fontFamily: "Inter, sans-serif",
        fontSize: "14px",
        backgroundColor: "white",
      },
      
      ...(enableImageUrl
        ? {
            uploader: {
              insertImageAsBase64URI: false,
            },
            image: {
              editAlt: true,
              editTitle: false,
            },
          }
        : {}),

      // Toolbar configuration - basic vs full
      buttonsXS: basicToolbar,
      
      buttons: showFullToolbar ? [
        // Full toolbar when expanded
        "bold", "italic", "underline", "strikethrough", "eraser", "|",
        "ul", "ol", "|",
        "font", "fontsize", "paragraph", "lineHeight", "|",
        "superscript", "subscript", "|",
        "indent", "outdent", "align", "symbols", "|",
        "table", "link", "hr", "brush", "image", "video", "|",
        "source", "spellcheck", "undo", "redo", "find", "cut", "copy", "paste"
      ] : basicToolbar,
      
      buttonsMD: showFullToolbar ? [
        "bold", "italic", "underline", "strikethrough", "eraser", "|",
        "ul", "ol", "|",
        "font", "fontsize", "paragraph", "lineHeight", "|",
        "superscript", "subscript", "|",
        "indent", "outdent", "align", "symbols", "|",
        "table", "link", "hr", "brush", "image", "video", "|",
        "source", "spellcheck", "undo", "redo", "find", "cut", "copy", "paste"
      ] : basicToolbar,
    };

    return editorConfig ? { ...defaultConfig, ...editorConfig } : defaultConfig;
  }, [minHeight, maxHeight, editorConfig, showFullToolbar, enableImageUrl]);

  const handleChange = (newContent: string) => {
    // Clean up iframe content if present
    if (newContent.includes('<iframe')) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(newContent, "text/html");
      const iframes = doc.querySelectorAll("iframe");
      
      iframes.forEach((iframe) => {
        if (iframe.getAttribute("width") !== "100%") {
          iframe.style.margin = "0 auto";
          iframe.style.display = "flex";
        }
      });
      
      const serializer = new XMLSerializer();
      newContent = serializer.serializeToString(doc);
    }
    
    onChange(newContent);
  };

  return (
    <div className="space-y-2 jodit-wrapper">
      {(enableAI || true) && (
        <div className="flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowFullToolbar(!showFullToolbar)}
            className="h-8 px-2"
            title={showFullToolbar ? "Show Less" : "Show More"}
          >
            {showFullToolbar ? "Less" : "More"}
          </Button>

          {enableAI && (
            <div className="relative">
              <AIAssistant
                fieldLabel={title}
                currentValue={value}
                onSelect={onChange}
                context={aiContext}
              />
            </div>
          )}
        </div>
      )}

      <div className="relative">
        <JoditEditor
          ref={editor}
          value={value}
          config={config}
          className={`w-full rounded-md ${showBorder ? 'border border-input' : ''} bg-background text-sm focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 ${className || ""}`}
          onBlur={handleChange}
          onChange={handleChange}
        />
      </div>
      
      {error && (
        <p className="text-xs text-destructive mt-1">{error}</p>
      )}
    </div>
  );
}

export default RichTextEditor;